import { checkPermissions } from "@/graphql/schemas/shared/permissions/permissions"
import { DomainObjects, Operations } from "@/graphql/schemas/shared/permissions/permissionsConfig"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { applyRounding } from "../utils/calculations"

export const queryAdmStatsByCustomerRanking = {
  async admStatsByCustomerRanking(_parent, args, context, _info) {
    const user = context?.user

    await checkPermissions({
      user,
      domainObject: DomainObjects.VOLUME,
      operation: Operations.READ,
    })

    const { admNumber, period1, period2 } = args
    const currentDate = new Date()
    const currentMonth = String(currentDate.getMonth() + 1)

    const {
      start: monthStart1 = "1",
      end: monthEnd1 = currentMonth,
      year: year1 = String(currentDate.getFullYear()),
    } = period1

    const {
      start: monthStart2 = "1",
      end: monthEnd2 = currentMonth,
      year: year2 = String(currentDate.getFullYear() - 1),
    } = period2

    // ------------- First period -------------
    // The first day of the month in "yyyy-mm-dd" format
    const start1 = `${year1}-${monthStart1.padStart(2, "0")}-01`

    // This is a workaround to get the last day of the month also with german summertime
    const endDate1 = new Date(year1, monthEnd1, 0).getDate()
    const end1 = `${year1}-${monthEnd1.padStart(2, "0")}-${endDate1}`

    // ------------- Second period -------------
    const start2 = `${year2}-${monthStart2.padStart(2, "0")}-01`
    const endDate2 = new Date(year2, monthEnd2, 0).getDate()
    const end2 = `${year2}-${monthEnd2.padStart(2, "0")}-${endDate2}`

    // *PeriodOne is the previous year (100%), *PeriodTwo is the current year
    const graphiQLQueryObject = [
      {
        method: "rankingStats: admStatsByCustomerRanking",
        variables: {
          startPeriodOne: start2,
          endPeriodOne: end2,
          startPeriodTwo: start1,
          endPeriodTwo: end1,
          admNumber: admNumber,
        },
        fields: [
          "customerRanking",
          "customerNumber",
          "customerName: companyName1",
          "companyName: companyName2",
          "tourNumber",
          "volumePast: volumeFirstPeriod",
          "volumeNow: volumeSecondPeriod",
          "percentageDeviation",
        ],
      },
    ]

    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const einzaResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.rankingStats

    if (!einzaResponse) throw new Error("Data could not be retrieved")

    // Array of unique customer rankings
    const rankingValues = [...new Set(einzaResponse.map((item) => item.customerRanking))]

    let totalSumPast = 0
    let totalSumNow = 0

    const sortedRankingStats = rankingValues?.map((ranking) => {
      const items = [...einzaResponse]
        .filter((item) => item.customerRanking === ranking)
        .sort((a, b) => b.volumeNow - a.volumeNow)

      const sumPast = items.reduce((sum, item) => sum + (item.volumePast || 0), 0)
      totalSumPast += sumPast
      const sumNow = items.reduce((sum, item) => sum + (item.volumeNow || 0), 0)
      totalSumNow += sumNow

      const sumDeviation = sumNow === 0 ? 0 : ((sumNow - sumPast) / sumPast) * 100

      return {
        customerList: items?.map((item) => ({
          ...item,
          percentageDeviation: String(applyRounding(item?.percentageDeviation)),
        })),
        ranking,
        sumPast: applyRounding(sumPast),
        sumNow: applyRounding(sumNow),
        sumDeviation: String(applyRounding(sumDeviation)),
      }
    })

    const totalDeviation = totalSumNow === 0 ? 0 : ((totalSumNow - totalSumPast) / totalSumPast) * 100

    return {
      items: sortedRankingStats,
      totalSumPast: applyRounding(totalSumPast),
      totalSumNow: applyRounding(totalSumNow),
      totalDeviation: String(applyRounding(totalDeviation)),
    }
  },
}
